import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useGetLocationDepthSeries } from '@/domain/depthSeries';
import type { PenOverview } from '@/domain/operationalDashboard';
import { usePostHogFeatureFlag } from '@/hooks/usePostHogFeatureFlag';

import PenOverviewCard from '../PenOverviewCard/PenOverviewCard';
import PenOverviewCardSkeleton from '../PenOverviewCard/PenOverviewCardSkeleton';
import { siteDepthWindow } from '../PenOverviewCard/penDepth';
import SectionTitle from '../SectionTitle/SectionTitle';

import styles from './PenOverviewSection.module.scss';

interface PenOverviewSectionProps {
    penOverview?: PenOverview;
    isLoading: boolean;
    expectedPenCount: number;
}

export default function PenOverviewSection(props: PenOverviewSectionProps) {
    const { penOverview, isLoading, expectedPenCount } = props;
    const { t } = useTranslation();
    const { clientId } = useParams<{ clientId: string }>();
    const { enabled: hourlyDepth } = usePostHogFeatureFlag('hourly-depth');
    const client = Number(clientId);
    const locationId = penOverview?.locationId ?? 0;
    const timeZone = penOverview?.timezone ?? '';
    const depthWindow = timeZone === '' ? null : siteDepthWindow(new Date(), timeZone);
    const depth = useGetLocationDepthSeries(
        Number.isFinite(client) ? client : 0,
        locationId,
        {
            from: depthWindow?.from ?? '',
            to: depthWindow?.to ?? '',
            resolution: 'hour',
        },
        {
            enabled: hourlyDepth && locationId > 0 && client > 0 && depthWindow !== null,
            staleTime: 30_000,
            refetchOnWindowFocus: true,
            refetchInterval: 60_000,
        }
    );
    const depthByCage = new Map(depth.data.cages.map((cage) => [cage.cageId, cage.points]));

    const skeletonKeys = Array.from(
        { length: Math.max(expectedPenCount, 1) },
        (_, i) => `pen-skeleton-${i}`
    );

    return (
        <div>
            <SectionTitle
                title={t('operationalDashboard.penOverview.title')}
                tooltip={t('operationalDashboard.penOverview.titleTooltip')}
            />
            <div className={styles.grid}>
                {isLoading || !penOverview
                    ? skeletonKeys.map((key) => <PenOverviewCardSkeleton key={key} />)
                    : penOverview.pens.map((pen) => (
                          <PenOverviewCard
                              key={pen.cageId}
                              pen={pen}
                              timeZone={timeZone}
                              depthPoints={
                                  hourlyDepth && timeZone !== ''
                                      ? (depthByCage.get(pen.cageId) ?? [])
                                      : undefined
                              }
                          />
                      ))}
            </div>
        </div>
    );
}
